import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, FormattedNumber, injectIntl, intlShape } from 'react-intl';
import injectSheet from 'react-jss';
import Button from '../../Button';
import { getTextColorsFromSection } from '../../lib/colors';
import Section from '../../Section';
import Typography from '../../Typography';
import Divider from '../../Divider';
import FormattedCurrency from '../../FormattedCurrency';
import credential from './credential.png';
import barcode from './barcode.png';

function OrderConfirmation({
  classes,
  intl,
  event,
  site,
  section,
  order,
  orderTicketsWithQuantity,
  onConfirm,
  theme,
}) {
  const {
    data: { button },
    styles,
  } = section;
  const { paymentForm, grandTotalValue, bankSlipUrl, bankSlipDueDate } = order;

  const colors = getTextColorsFromSection(site, section);
  const isCreditCard = paymentForm === 'creditCard';

  return (
    <Section
      site={site}
      section={section}
      rootClassName={classes.sectionRoot}
      className={classes.root}
    >
      <Typography
        type="title"
        className={classes.title}
        style={{ ...colors.title, ...section.styles.title }}
      >
        <FormattedMessage
          id={isCreditCard ? 'creditCardConfirmationTitle' : 'bankSlipConfirmationTitle'}
        />
      </Typography>
      <Typography className={classes.message} type="caption">
        <FormattedMessage
          id={
            !grandTotalValue
              ? 'freeConfirmationMessage'
              : isCreditCard
              ? 'creditCardConfirmationMessage'
              : 'bankSlipConfirmationMessage'
          }
          values={{ bankSlipDueDate: new Date(bankSlipDueDate) }}
        />
      </Typography>
      <div className={classes.imageAndSummaryWrapper}>
        {isCreditCard ? (
          <div className={classes.imageWrapper}>
            <img
              className={classes.credential}
              src={credential}
              alt={intl.formatMessage({ id: 'credential' })}
            />
          </div>
        ) : (
          <div className={classes.bankSlip}>
            <div className={classes.imageWrapper}>
              <img src={barcode} alt={intl.formatMessage({ id: 'barcode' })} />
            </div>
            <Button
              event={event}
              site={site}
              section={section}
              component="a"
              href={bankSlipUrl}
              target="_blank"
              style={{ ...styles.button, backgroundColor: theme.palette.success[500] }}
            >
              <FormattedMessage id="printBankSlip" />
            </Button>
          </div>
        )}
        <div className={classes.summary}>
          {orderTicketsWithQuantity.map(ticket => (
            <Typography key={ticket.id} type="body2">
              <FormattedNumber value={ticket.quantity} /> x {ticket.name}
            </Typography>
          ))}
          <Divider />
          <Typography>
            <FormattedMessage id="confirmationOrderTotal" />{' '}
            <Typography type="body2" component="span">
              {grandTotalValue === 0 ? (
                <Typography className={classes.freeTicket}>
                  <FormattedMessage id="freeTicket" />
                </Typography>
              ) : (
                <FormattedCurrency value={grandTotalValue} />
              )}
            </Typography>
          </Typography>
        </div>
      </div>
      {!isCreditCard && (
        <Typography type="caption" paragraph>
          <FormattedMessage id="bankSlipConfirmationDelayInstructions" />
        </Typography>
      )}
      <Typography type="caption" paragraph>
        <FormattedMessage
          id={
            isCreditCard
              ? 'creditCardConfirmationEmailSent'
              : 'bankSlipConfirmationEmailInstructions'
          }
        />
      </Typography>
      {!isCreditCard && (
        <Typography type="caption" paragraph>
          <FormattedMessage id="bankSlipConfirmationDueInstructions" />
        </Typography>
      )}
      <Button
        event={event}
        site={site}
        section={section}
        button={button}
        style={{ ...styles.button, backgroundColor: theme.palette.success[500] }}
        className={classes.button}
        onClick={onConfirm}
      >
        <FormattedMessage id="backToSite" />
      </Button>
    </Section>
  );
}

OrderConfirmation.propTypes = {
  classes: PropTypes.object.isRequired,
  intl: intlShape.isRequired,
  event: PropTypes.object.isRequired,
  site: PropTypes.object.isRequired,
  section: PropTypes.object.isRequired,
  order: PropTypes.object.isRequired,
  onConfirm: PropTypes.func.isRequired,
  orderTicketsWithQuantity: PropTypes.array.isRequired,
  theme: PropTypes.object.isRequired,
};

const stylesheet = theme => ({
  sectionRoot: {
    height: '100%',
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing.large,
    height: '100%',
    overflow: 'auto',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing.medium,
    },
  },
  title: {
    gridArea: 'title',
    paddingRight: theme.spacing.large,
    marginBottom: theme.spacing.medium,
  },
  freeTicket: {
    color: theme.palette.success[500],
    fontWeight: 'bold',
  },
  message: {
    marginBottom: theme.spacing.medium,
  },
  imageAndSummaryWrapper: {
    marginBottom: theme.spacing.medium,
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
      alignItems: 'center',
    },
  },
  imageWrapper: {
    flex: '1 2 auto',
  },
  credential: {
    display: 'block',
    width: 164,
    margin: [[0, 'auto']],
    [theme.breakpoints.down('xs')]: {
      width: 120,
    },
  },
  bankSlip: {
    flex: '1 2 auto',
    boxShadow: theme.shadows[2],
    padding: theme.spacing.medium,
    textAlign: 'center',
  },
  summary: {
    flex: '2 1 auto',
    marginTop: theme.spacing.medium,
    [theme.breakpoints.up('sm')]: {
      marginTop: 0,
      marginLeft: theme.spacing.large,
    },
  },
  button: {
    marginTop: theme.spacing.medium,
    flex: '0 0 auto',
    alignSelf: 'flex-end',
    margin: 0,
  },
});

export default compose(
  injectIntl,
  injectSheet(stylesheet),
)(OrderConfirmation);



// WEBPACK FOOTER //
// ./src/site-render/src/RegisterExtension/OrderConfirmation/OrderConfirmation.js